// View 5: Utilities Selection

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UtilitySelector } from '@/components/plans';
import { LoadingOverlay } from '@/components/common';
import { useOrderStore } from '@/stores/orderStore';
import { useFlowNavigation } from '@/hooks';
import { checkWaterServiceability, fetchOnitWaterFiltrationPlan } from '@/services/serviceabilityService';
import { fetchInternetPlans, fetchElectricityPlans } from '@/services/plansService';
import { syncCartWithSelections } from '@/services/cartService';
import { track } from '@/utils/analytics';
import { logger } from '@/utils/logger';
import {
  USAGE_MULTIPLIERS,
  calculateElectricityUsage,
  generateSyntheticUsage,
} from '@/utils/planCalculations';
import { normalizeSchedule } from '@/utils/scheduling';
import { logSinglePlanSchedule } from '@/utils/scheduleLogging';
import type { UsageLevel } from '@/utils/planCalculations';
import type { WaterPlan, InternetPlan, ElectricityPlan } from '@/types';

export function UtilitiesSelectionView() {
  const navigate = useNavigate();
  const {
    address,
    userId,
    propertyData,
    personalInfo,
    selectedServices,
    addService,
    removeService,
    setPlanOptionsQueue,
    waterAvailable,
    setWaterAvailable,
    waterDeclined,
    setWaterDeclined,
    onitSelected,
    setOnitPlan,
    setOnitSelected,
    isApartment,
    apartmentWaterChoice,
    setApartmentWaterChoice,
  } = useOrderStore();
  const { navigateFromUtilities, flowType } = useFlowNavigation();

  // Double-click protection
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Derive isBusiness/isRental from personalInfo (defaults to false if not yet filled)
  const isBusiness = personalInfo?.propertyType === 'Business';
  const isRental = personalInfo?.rentOrOwn === 'Rent';

  // Usage level state for electricity plans
  const [usageLevel, setUsageLevel] = useState<UsageLevel>('moderate');

  // Calculate base estimated usage from property data
  const baseEstimatedUsage = useMemo(() => {
    if (propertyData?.squareFootage) {
      return calculateElectricityUsage(propertyData.squareFootage, propertyData.yearBuilt);
    }
    return 1000; // Default fallback
  }, [propertyData]);

  // Apply usage level multiplier
  const estimatedUsage = useMemo(() => {
    return Math.round(baseEstimatedUsage * USAGE_MULTIPLIERS[usageLevel]);
  }, [baseEstimatedUsage, usageLevel]);

  // Generate synthetic 12-month usage array
  const usageArray = useMemo(() => {
    return generateSyntheticUsage(estimatedUsage);
  }, [estimatedUsage]);

  // Fetch water plans - must complete before user can continue
  const { data: waterResult, isLoading: isLoadingWater, isError: isWaterError, refetch: refetchWater } = useQuery({
    queryKey: ['waterPlans', address?.zip, isBusiness, isRental],
    queryFn: () => checkWaterServiceability(address!, userId || undefined, isBusiness, isRental),
    enabled: !!address?.zip && waterAvailable,
    retry: 3,
  });

  // Fetch internet plans - pass userId for 2TurnItOn API
  const { data: internetResult, isLoading: isLoadingInternet } = useQuery({
    queryKey: ['internetPlans', address?.fullAddress, userId, isBusiness, isRental],
    queryFn: () => fetchInternetPlans(address!, userId || undefined, isBusiness, isRental),
    enabled: !!address,
  });

  // Fetch electricity plans - pass userId and estimatedUsage
  // Refetches when usage level changes (which changes estimatedUsage)
  const { data: electricityResult, isLoading: isLoadingElectricity, isFetching: isFetchingElectricity } = useQuery({
    queryKey: ['electricityPlans', address?.zip, userId, estimatedUsage, isBusiness, isRental],
    queryFn: () => fetchElectricityPlans(address!, propertyData, null, userId || undefined, estimatedUsage, isBusiness, isRental),
    enabled: !!address?.zip,
  });

  // Fetch ONIT Water Filtration plan when Water is available
  const { data: onitPlanData } = useQuery({
    queryKey: ['onitWaterFiltration', address?.zip, userId, isBusiness, isRental],
    queryFn: () => fetchOnitWaterFiltrationPlan(address!.zip, userId || undefined, isBusiness, isRental),
    enabled: !!address?.zip && waterAvailable,
  });

  // Store the ONIT plan in state when fetched, or reset when not serviceable
  useEffect(() => {
    if (onitPlanData) {
      setOnitPlan(onitPlanData);
    } else if (onitPlanData === null) {
      setOnitPlan(null);
      setOnitSelected(false);
    }
  }, [onitPlanData, setOnitPlan, setOnitSelected]);

  // Update store when the re-fetch with isBusiness/isRental comes back unavailable
  useEffect(() => {
    if (waterResult && !waterResult.waterAvailable) {
      setWaterAvailable(false);
    }
  }, [waterResult, setWaterAvailable]);

  const waterPlans: WaterPlan[] = waterResult?.serviceData?.planData || [];
  const internetPlans: InternetPlan[] = internetResult?.plans || [];
  const electricityPlans: ElectricityPlan[] = electricityResult?.plans || [];

  // Show loading overlay until ALL initial utility data has loaded
  const isLoadingUtilities = isLoadingWater || isLoadingElectricity || isLoadingInternet;

  // Handlers for service selection
  const handleSelectWater = (plan: WaterPlan | null) => {
    const existing = selectedServices.find((s) => s.type === 'water');
    if (existing) removeService(existing.planId);

    if (plan) {
      const schedule = normalizeSchedule(plan);
      logSinglePlanSchedule({
        area: 'utilities',
        serviceName: 'Water',
        stage: 'service-selected',
        zipCode: address?.zip,
        userId: userId || undefined,
        plan,
      });
      addService({
        type: 'water',
        planId: plan.id || plan.ID || 'water-1',
        vendorId: plan.vendorId,
        vendorName: plan.vendorName,
        name: plan.name,
        leadTime: schedule.leadTime,
        isSunday: schedule.isSunday,
        isMonday: schedule.isMonday,
        isTuesday: schedule.isTuesday,
        isWednesday: schedule.isWednesday,
        isThursday: schedule.isThursday,
        isFriday: schedule.isFriday,
        isSaturday: schedule.isSaturday,
      });
      // User selected water, clear declined flag
      setWaterDeclined(false);
      track('checkout_service_added', {
        service_type: 'water',
        plan_name: plan.name,
        vendor_name: plan.vendorName,
        zip: address?.zip,
        flow_type: flowType,
      });
    } else {
      // User explicitly declined water
      setWaterDeclined(true);
      track('checkout_service_removed', {
        service_type: 'water',
        zip: address?.zip,
        flow_type: flowType,
      });
      // If deselecting Water, also remove ONIT
      const onitService = selectedServices.find((s) => s.type === 'waterFiltration');
      if (onitService) {
        removeService(onitService.planId);
      }
      setOnitSelected(false);
    }
  };

  const handleSelectInternet = (plan: InternetPlan | null) => {
    const existing = selectedServices.find((s) => s.type === 'internet');
    if (existing) removeService(existing.planId);

    if (plan) {
      const schedule = normalizeSchedule(plan);
      logSinglePlanSchedule({
        area: 'utilities',
        serviceName: 'Internet',
        stage: 'service-selected',
        zipCode: address?.zip,
        userId: userId || undefined,
        plan,
      });
      addService({
        type: 'internet',
        planId: plan.id || plan.ID || '',
        vendorId: plan.vendorId,
        vendorName: plan.vendorName,
        name: plan.name,
        leadTime: schedule.leadTime,
        isSunday: schedule.isSunday,
        isMonday: schedule.isMonday,
        isTuesday: schedule.isTuesday,
        isWednesday: schedule.isWednesday,
        isThursday: schedule.isThursday,
        isFriday: schedule.isFriday,
        isSaturday: schedule.isSaturday,
      });
      track('checkout_service_added', {
        service_type: 'internet',
        plan_name: plan.name,
        vendor_name: plan.vendorName,
        zip: address?.zip,
        flow_type: flowType,
      });
    } else {
      track('checkout_service_removed', {
        service_type: 'internet',
        zip: address?.zip,
        flow_type: flowType,
      });
    }
  };

  const handleSelectElectricity = (plan: ElectricityPlan | null) => {
    const existing = selectedServices.find((s) => s.type === 'electricity');
    if (existing) removeService(existing.planId);

    if (plan) {
      const schedule = normalizeSchedule(plan);
      logSinglePlanSchedule({
        area: 'utilities',
        serviceName: 'Electricity',
        stage: 'service-selected',
        zipCode: address?.zip,
        userId: userId || undefined,
        plan,
      });
      addService({
        type: 'electricity',
        planId: plan.id || plan.ID || '',
        vendorId: plan.vendorId,
        vendorName: plan.vendorName,
        name: plan.name,
        leadTime: schedule.leadTime,
        isSunday: schedule.isSunday,
        isMonday: schedule.isMonday,
        isTuesday: schedule.isTuesday,
        isWednesday: schedule.isWednesday,
        isThursday: schedule.isThursday,
        isFriday: schedule.isFriday,
        isSaturday: schedule.isSaturday,
      });
      track('checkout_service_added', {
        service_type: 'electricity',
        plan_name: plan.name,
        vendor_name: plan.vendorName,
        zip: address?.zip,
        flow_type: flowType,
      });
    } else {
      track('checkout_service_removed', {
        service_type: 'electricity',
        zip: address?.zip,
        flow_type: flowType,
      });
    }
  };

  const handleContinue = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Only sync cart for Flow A - in Flow B, cart doesn't exist yet (created by submitGetStarted)
      if (flowType === 'A' && userId) {
        const cartSelections = selectedServices.map((s) => ({
          planId: s.planId,
          vendorId: s.vendorId,
          serviceName: s.type.charAt(0).toUpperCase() + s.type.slice(1), // 'water' -> 'Water'
          options: s.options,
        }));
        await syncCartWithSelections(userId, cartSelections);
      }

      // Set up plan options queue (only for Flow A, since Flow B configures after Personal Info)
      const planIds = selectedServices.map((s) => s.planId);

      logger.info(
        { area: 'utilities', userId: userId || undefined },
        'Continuing from utilities with selected services',
        {
          zipCode: address?.zip,
          flowType,
          planIds,
          selectedServices: selectedServices.map((service) => ({
            type: service.type,
            planId: service.planId,
            vendorId: service.vendorId,
            vendorName: service.vendorName,
            leadTime: service.leadTime,
            isSaturday: service.isSaturday,
            isSunday: service.isSunday,
            isMonday: service.isMonday,
            isTuesday: service.isTuesday,
            isWednesday: service.isWednesday,
            isThursday: service.isThursday,
            isFriday: service.isFriday,
          })),
        }
      );

      if (flowType === 'A' && planIds.length > 0) {
        setPlanOptionsQueue(planIds);
      }

      const serviceTypes = selectedServices.map(s => s.type);
      track('checkout_utilities_continued', {
        zip: address?.zip,
        flow_type: flowType,
        services: serviceTypes,
        total_services: serviceTypes.length,
        has_water: serviceTypes.includes('water'),
        has_electricity: serviceTypes.includes('electricity'),
        has_internet: serviceTypes.includes('internet'),
        water_declined: waterDeclined,
        onit_selected: onitSelected,
      });

      // Use flow-aware navigation
      // Flow A: goes to /plan-options/{planId} or /checkout
      // Flow B: goes to /personal-info
      navigateFromUtilities(planIds);
    } catch (err) {
      track('checkout_error', {
        error_context: 'cart_sync',
        error_message: err instanceof Error ? err.message : String(err),
      });
      // Reset on error so user can retry
      setIsSubmitting(false);
    }
  };

  // Check availability
  const hasWater = waterAvailable && waterPlans.length > 0;
  const hasInternet = internetPlans.length > 0;
  const hasElectricity = electricityPlans.length > 0;
  const showWaterUnavailable = !waterAvailable;
  const hasAnyUtility = hasWater || hasInternet || hasElectricity || showWaterUnavailable;

  return (
    <>
      {isLoadingUtilities && (
        <LoadingOverlay message="Finding the best plans for your home" />
      )}
      <section className="view" style={{ display: 'block' }}>
      <div className="container">
        <h1 className="text--center mb-6">Select utilities for set up</h1>

        <UtilitySelector
          waterPlans={waterPlans}
          internetPlans={internetPlans}
          electricityPlans={electricityPlans}
          estimatedUsage={estimatedUsage}
          usageArray={usageArray}
          usageLevel={usageLevel}
          onUsageLevelChange={setUsageLevel}
          squareFootage={propertyData?.squareFootage}
          isLoadingElectricity={isFetchingElectricity}
          selectedServices={selectedServices}
          onSelectWater={handleSelectWater}
          onSelectInternet={handleSelectInternet}
          onSelectElectricity={handleSelectElectricity}
          waterAvailable={hasWater}
          internetAvailable={hasInternet}
          electricityAvailable={hasElectricity}
          isApartment={isApartment}
          apartmentWaterChoice={apartmentWaterChoice}
          onApartmentWaterChoiceChange={setApartmentWaterChoice}
          waterDeclined={waterDeclined}
          showWaterUnavailable={showWaterUnavailable}
        />

        {/* Water fetch error banner */}
        {waterAvailable && isWaterError && (
          <div className="alert alert--error mb-4" role="alert">
            <p>We couldn't load water plans. Please check your connection and try again.</p>
            <button
              type="button"
              className="btn btn--small mt-2"
              onClick={() => refetchWater()}
            >
              RETRY WATER
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-row">
          <button type="button" className="btn btn--back" onClick={() => navigate(-1)}>
            GO BACK
          </button>
          {selectedServices.length > 0 ? (
            <button
              type="button"
              className="btn"
              onClick={handleContinue}
              disabled={isSubmitting || (waterAvailable && (isLoadingWater || isWaterError))}
            >
              {isSubmitting || (waterAvailable && isLoadingWater) ? 'LOADING...' : 'CONTINUE'}
            </button>
          ) : hasAnyUtility ? (
            <button
              type="button"
              className="btn"
              disabled
            >
              SELECT A SERVICE TO CONTINUE
            </button>
          ) : null}
        </div>
      </div>
      </section>
    </>
  )
}
